import React from 'react';
import { GRAPHIC_BORDER_PRESETS } from '../graphicBorders.js';
import MediaInput from '../../components/MediaInput.jsx';

export const GraphicBorderControl = ({
    prefix = 'shopImage',
    styleSettings = {},
    handleStyleChange,
    title = 'Graphic Border & Decorative Frame'
}) => {
    const borderKey = `${prefix}GraphicBorder`;
    const urlKey = `${prefix}GraphicBorderUrl`;
    const widthKey = `${prefix}GraphicBorderWidth`;
    const sliceKey = `${prefix}GraphicBorderSlice`;
    const repeatKey = `${prefix}GraphicBorderRepeat`;
    const outsetKey = `${prefix}GraphicBorderOutset`;
    const colorKey = `${prefix}GraphicBorderColor`;
    const paddingKey = `${prefix}GraphicBorderPadding`;

    const currentPresetId = styleSettings[borderKey] || 'none';
    const currentPreset = GRAPHIC_BORDER_PRESETS.find(p => p.id === currentPresetId) || GRAPHIC_BORDER_PRESETS[0];
    const isCustom = currentPresetId === 'custom';
    const isActive = currentPresetId !== 'none';

    const currentWidth = styleSettings[widthKey] || currentPreset.defaultWidth || '16px';
    const currentPadding = styleSettings[paddingKey] ?? (currentPreset.defaultPadding || '10px');
    const currentColor = styleSettings[colorKey] || currentPreset.defaultColor || '#d97736';
    const currentRepeat = styleSettings[repeatKey] || currentPreset.repeat || 'round';
    const currentSlice = styleSettings[sliceKey] || currentPreset.slice || '30';
    const currentUrl = styleSettings[urlKey] || '';

    // Apply preset with defaults
    const handleSelectPreset = (presetId) => {
        handleStyleChange(borderKey, presetId);
        const preset = GRAPHIC_BORDER_PRESETS.find(p => p.id === presetId);
        if (preset && preset.id !== 'none') {
            if (preset.defaultWidth) handleStyleChange(widthKey, preset.defaultWidth);
            if (preset.defaultPadding) handleStyleChange(paddingKey, preset.defaultPadding);
            if (preset.defaultColor) handleStyleChange(colorKey, preset.defaultColor);
            if (preset.repeat) handleStyleChange(repeatKey, preset.repeat);
            if (preset.slice) handleStyleChange(sliceKey, preset.slice);
        }
    };

    const handleClear = () => {
        handleStyleChange(borderKey, 'none');
    };

    return (
        <div style={{
            background: isActive ? '#f8fafc' : '#ffffff',
            border: isActive ? '1px solid #93c5fd' : '1px solid #e2e8f0',
            borderRadius: '8px',
            padding: '10px 12px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            transition: 'all 0.2s ease',
            boxShadow: isActive ? '0 2px 6px -1px rgba(37,99,235,0.08)' : 'none'
        }}>
            {/* Header with Title and Status */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '6px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '0.85rem' }}>🖼️</span>
                    <span style={{ fontSize: '0.75rem', fontWeight: '700', color: '#1e293b' }}>
                        {title}
                    </span>
                </div>
                {isActive && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{
                            fontSize: '0.65rem',
                            fontWeight: '700',
                            background: '#dbeafe',
                            color: '#1d4ed8',
                            padding: '2px 7px',
                            borderRadius: '10px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em'
                        }}>
                            Active Frame
                        </span>
                        <button
                            type="button"
                            onClick={handleClear}
                            title="Remove Graphic Border"
                            style={{
                                background: '#fee2e2',
                                border: '1px solid #fca5a5',
                                color: '#b91c1c',
                                borderRadius: '4px',
                                padding: '2px 6px',
                                fontSize: '0.65rem',
                                fontWeight: '700',
                                cursor: 'pointer'
                            }}
                        >
                            Clear
                        </button>
                    </div>
                )}
            </div>

            {/* Preset Selector Dropdown */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '0.7rem', fontWeight: '600', color: '#475569' }}>
                    Frame Style Preset
                </label>
                <select
                    value={currentPresetId}
                    onChange={(e) => handleSelectPreset(e.target.value)}
                    style={{
                        width: '100%',
                        padding: '6px 8px',
                        borderRadius: '5px',
                        border: '1px solid #cbd5e1',
                        fontSize: '0.75rem',
                        background: '#ffffff',
                        fontWeight: '600',
                        color: '#0f172a',
                        boxSizing: 'border-box'
                    }}
                >
                    {GRAPHIC_BORDER_PRESETS.map((p) => (
                        <option key={p.id} value={p.id}>
                            {p.name}
                        </option>
                    ))}
                </select>
            </div>

            {/* Quick-Select Preset Chips */}
            <div style={{
                display: 'flex',
                gap: '5px',
                overflowX: 'auto',
                paddingBottom: '2px',
                scrollbarWidth: 'thin'
            }}>
                {GRAPHIC_BORDER_PRESETS.map((p) => {
                    const isSelected = currentPresetId === p.id;
                    const shortName = p.name.split(' (')[0];
                    return (
                        <button
                            key={p.id}
                            type="button"
                            onClick={() => handleSelectPreset(p.id)}
                            style={{
                                flexShrink: 0,
                                padding: '3px 8px',
                                fontSize: '0.65rem',
                                fontWeight: isSelected ? '700' : '500',
                                borderRadius: '4px',
                                border: isSelected ? '1px solid #2563eb' : '1px solid #cbd5e1',
                                background: isSelected ? '#eff6ff' : '#ffffff',
                                color: isSelected ? '#1d4ed8' : '#475569',
                                cursor: 'pointer',
                                transition: 'all 0.15s ease'
                            }}
                            title={p.desc}
                        >
                            {shortName}
                        </button>
                    );
                })}
            </div>

            {/* Active Preset Description */}
            {currentPreset.desc && (
                <div style={{
                    fontSize: '0.68rem',
                    color: '#64748b',
                    fontStyle: 'italic',
                    lineHeight: '1.3'
                }}>
                    ℹ️ {currentPreset.desc}
                </div>
            )}

            {/* Expanded Controls when Graphic Border is Active */}
            {isActive && (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    paddingTop: '6px',
                    borderTop: '1px solid #e2e8f0'
                }}>
                    {/* Width and Frame Inset Padding */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                        <label style={{ fontSize: '0.7rem', fontWeight: '600', color: '#475569' }}>
                            Border Width
                            <input
                                type="text"
                                value={styleSettings[widthKey] ?? currentPreset.defaultWidth ?? '16px'}
                                onChange={(e) => handleStyleChange(widthKey, e.target.value)}
                                placeholder="18px"
                                style={{
                                    width: '100%',
                                    padding: '5px 8px',
                                    marginTop: '3px',
                                    borderRadius: '4px',
                                    border: '1px solid #cbd5e1',
                                    fontSize: '0.75rem',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </label>
                        <label style={{ fontSize: '0.7rem', fontWeight: '600', color: '#475569' }}>
                            Inner Inset Padding
                            <input
                                type="text"
                                value={styleSettings[paddingKey] ?? currentPreset.defaultPadding ?? '12px'}
                                onChange={(e) => handleStyleChange(paddingKey, e.target.value)}
                                placeholder="12px"
                                style={{
                                    width: '100%',
                                    padding: '5px 8px',
                                    marginTop: '3px',
                                    borderRadius: '4px',
                                    border: '1px solid #cbd5e1',
                                    fontSize: '0.75rem',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </label>
                    </div>

                    {/* Tint Color & Repeat Mode */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                        <label style={{ fontSize: '0.7rem', fontWeight: '600', color: '#475569' }}>
                            Tint / Frame Color
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '3px' }}>
                                <input
                                    type="color"
                                    value={currentColor && currentColor.startsWith('#') ? currentColor : '#d97736'}
                                    onChange={(e) => handleStyleChange(colorKey, e.target.value)}
                                    style={{
                                        width: '28px',
                                        height: '28px',
                                        padding: '1px',
                                        borderRadius: '3px',
                                        border: '1px solid #cbd5e1',
                                        cursor: 'pointer',
                                        flexShrink: 0
                                    }}
                                />
                                <input
                                    type="text"
                                    value={styleSettings[colorKey] ?? ''}
                                    onChange={(e) => handleStyleChange(colorKey, e.target.value)}
                                    placeholder={currentPreset.defaultColor || '#d97736'}
                                    style={{
                                        flex: 1,
                                        minWidth: 0,
                                        padding: '4px 6px',
                                        borderRadius: '4px',
                                        border: '1px solid #cbd5e1',
                                        fontSize: '0.75rem',
                                        boxSizing: 'border-box',
                                        height: '28px'
                                    }}
                                />
                            </div>
                        </label>
                        <label style={{ fontSize: '0.7rem', fontWeight: '600', color: '#475569' }}>
                            Border Repeat Mode
                            <select
                                value={currentRepeat}
                                onChange={(e) => handleStyleChange(repeatKey, e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '5px 8px',
                                    marginTop: '3px',
                                    borderRadius: '4px',
                                    border: '1px solid #cbd5e1',
                                    fontSize: '0.75rem',
                                    background: '#ffffff',
                                    boxSizing: 'border-box',
                                    height: '28px'
                                }}
                            >
                                <option value="round">Round (Tiled & Scaled)</option>
                                <option value="stretch">Stretch (Smooth Vector)</option>
                                <option value="repeat">Repeat (Tiled)</option>
                                <option value="space">Space (Even Gaps)</option>
                            </select>
                        </label>
                    </div>

                    {/* Custom Graphic Border Input (when 'custom' preset is selected or custom URL is present) */}
                    {(isCustom || currentUrl) && (
                        <div style={{
                            background: '#f1f5f9',
                            padding: '8px',
                            borderRadius: '6px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '6px',
                            border: '1px dashed #cbd5e1'
                        }}>
                            <label style={{ fontSize: '0.7rem', fontWeight: '700', color: '#334155' }}>
                                Custom Border Graphic Image / SVG
                            </label>
                            <MediaInput
                                value={currentUrl}
                                onChange={(e) => handleStyleChange(urlKey, e.target.value)}
                                placeholder="Paste image URL or pick from media..."
                            />
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                                <label style={{ fontSize: '0.68rem', fontWeight: '600', color: '#475569' }}>
                                    9-Slice Cutout
                                    <input
                                        type="text"
                                        value={styleSettings[sliceKey] ?? '30'}
                                        onChange={(e) => handleStyleChange(sliceKey, e.target.value)}
                                        placeholder="30 or 25% fill"
                                        style={{
                                            width: '100%',
                                            padding: '4px 6px',
                                            borderRadius: '3px',
                                            border: '1px solid #cbd5e1',
                                            fontSize: '0.72rem',
                                            boxSizing: 'border-box'
                                        }}
                                    />
                                </label>
                                <label style={{ fontSize: '0.68rem', fontWeight: '600', color: '#475569' }}>
                                    Outset Distance
                                    <input
                                        type="text"
                                        value={styleSettings[outsetKey] ?? '0px'}
                                        onChange={(e) => handleStyleChange(outsetKey, e.target.value)}
                                        placeholder="0px"
                                        style={{
                                            width: '100%',
                                            padding: '4px 6px',
                                            borderRadius: '3px',
                                            border: '1px solid #cbd5e1',
                                            fontSize: '0.72rem',
                                            boxSizing: 'border-box'
                                        }}
                                    />
                                </label>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default GraphicBorderControl;
